import type { GoalAssigneeType, GoalMetric, GoalResponse } from '@troia-v3/types';
import type { IconName } from '@/shared/icons';

/** Rótulos das métricas (pt-BR). */
export const METRIC_LABEL: Record<GoalMetric, string> = {
  revenue: 'Receita',
  deals: 'Negócios fechados',
  calls: 'Ligações realizadas',
  meetings: 'Reuniões realizadas',
  qualifiedLeads: 'Leads qualificados',
  proposalsSent: 'Propostas enviadas',
};

/** Ícone (catálogo `@/shared/icons`) por métrica. */
export const METRIC_ICON: Record<GoalMetric, IconName> = {
  revenue: 'dollar-sign',
  deals: 'trending-up',
  calls: 'phone',
  meetings: 'calendar',
  qualifiedLeads: 'user-check',
  proposalsSent: 'file-text',
};

/** Rótulo curto do tipo de responsável (usado em filtros/chips). */
export const ASSIGNEE_TYPE_LABEL: Record<GoalAssigneeType, string> = {
  user: 'Vendedor',
  team: 'Equipe',
  company: 'Empresa',
};

/** Sublabel descritivo (usado abaixo do nome do responsável). */
export const ASSIGNEE_SUBLABEL: Record<GoalAssigneeType, string> = {
  user: 'Vendedor',
  team: 'Equipe',
  company: 'Meta da empresa',
};

/** Rótulo do período (granularidade). */
export const PERIOD_LABEL: Record<string, string> = {
  month: 'Mensal',
  quarter: 'Trimestral',
  year: 'Anual',
};

export function isCompanyGoal(goal: GoalResponse): boolean {
  return goal.assigneeType === 'company';
}

/** Formata a janela [start, end] como "dd/mm/aaaa → dd/mm/aaaa". */
export function formatPeriodRange(start: Date | string, end: Date | string): string {
  const fmt = (v: Date | string): string => {
    const iso = String(v).slice(0, 10);
    const [y, m, d] = iso.split('-');
    return y && m && d ? `${d}/${m}/${y}` : iso;
  };
  return `${fmt(start)} → ${fmt(end)}`;
}

/** Formata o valor-alvo: moeda BRL para receita, número pt-BR para os demais. */
export function formatTarget(metric: GoalMetric, value: number): string {
  if (metric === 'revenue') {
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }
  return value.toLocaleString('pt-BR');
}

/** Iniciais do responsável (até 2 palavras). Company/vazio → "•". */
export function getAssigneeInitials(goal: GoalResponse): string {
  const name = goal.assigneeName?.trim();
  if (!name) return '•';
  const parts = name.split(/\s+/).filter(Boolean);
  const first = parts[0]?.charAt(0) ?? '';
  const second = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? '') : '';
  return (first + second).toUpperCase() || '•';
}
